import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Image as ImageIcon, Loader2, Save, Store, Upload } from "lucide-react";
import { toast } from "sonner";

import SedecShell from "@/components/layout/SedecShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useListSedecEcoleOptions } from "@/lib/backoffice-hooks";
import {
  RUBRIQUES_VITRINE,
  VITRINE_TEXTE_MAX,
  getVitrine,
  putVitrine,
  uploadVitrinePhoto,
  type RubriqueVitrine,
} from "@/lib/api";
import { friendlyError } from "@/lib/errors";

/**
 * Vitrines du réseau — le SEDEC renseigne la fiche publique de n'importe quel
 * établissement.
 *
 * <p>Le pendant de la page « Vitrine » de CATHO SCHOOL PRO, où chaque école ne
 * voit que la sienne. Ici, on choisit l'école dans la liste du réseau : c'est
 * ce qui permet de dépanner un établissement qui n'a pas encore pris la main,
 * ou de préparer les fiches avant une formation de directeurs.</p>
 */
export default function SedecVitrines() {
  const qc = useQueryClient();
  const { data: ecoles = [], isLoading: chargementEcoles } = useListSedecEcoleOptions();
  const [ecoleId, setEcoleId] = useState("");
  // Remonter l'input vide après un envoi : plus simple et plus sûr que de
  // remettre sa valeur à la main via une référence.
  const [cleInput, setCleInput] = useState(0);

  useEffect(() => {
    if (!ecoleId && ecoles.length > 0) setEcoleId(String(ecoles[0].id));
  }, [ecoles, ecoleId]);

  const { data: vitrine, isLoading } = useQuery({
    queryKey: ["sedec", "vitrine", ecoleId],
    queryFn: () => getVitrine(ecoleId),
    enabled: !!ecoleId,
  });

  // Les six clés dès le départ : indexer une clé absente obligerait à un repli
  // partout, et un repli est un chemin qu'on oublie de tester.
  const [textes, setTextes] = useState<Record<string, string>>(() =>
    Object.fromEntries(RUBRIQUES_VITRINE.map((r) => [r.cle, ""])),
  );
  const [enregistrement, setEnregistrement] = useState(false);
  const [envoiPhoto, setEnvoiPhoto] = useState(false);

  // Hydratation sur les VALEURS et non sur l'objet, dont l'identité change à
  // chaque rendu — sinon la saisie en cours serait écrasée en boucle.
  const signature = JSON.stringify(vitrine?.rubriques ?? {});
  useEffect(() => {
    const source = (vitrine?.rubriques ?? {}) as Record<string, RubriqueVitrine>;
    const initial: Record<string, string> = {};
    for (const r of RUBRIQUES_VITRINE) initial[r.cle] = source[r.cle]?.texte ?? "";
    setTextes(initial);
  }, [signature]);

  const remplies = RUBRIQUES_VITRINE.filter((r) => textes[r.cle].trim().length > 0).length;

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      const charge: Record<string, RubriqueVitrine> = {};
      for (const r of RUBRIQUES_VITRINE) charge[r.cle] = { texte: textes[r.cle] };
      await putVitrine(ecoleId, charge);
      await qc.invalidateQueries({ queryKey: ["sedec", "vitrine", ecoleId] });
      toast.success("Vitrine enregistrée");
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setEnregistrement(false);
    }
  };

  const envoyerPhoto = async (fichier?: File) => {
    if (!fichier) return;
    setEnvoiPhoto(true);
    try {
      await uploadVitrinePhoto(ecoleId, fichier);
      await qc.invalidateQueries({ queryKey: ["sedec", "vitrine", ecoleId] });
      toast.success("Photo mise en ligne");
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setEnvoiPhoto(false);
      setCleInput((n) => n + 1);
    }
  };

  return (
    <SedecShell>
      <div className="p-6 space-y-4">
        <div>
          <h2 className="text-[18px] font-bold text-[#0F1A3C]">Vitrines des établissements</h2>
          <p className="text-[12px] text-gray-400 mt-0.5">
            La fiche publique que les parents découvrent en scannant l'affiche d'une école.
          </p>
        </div>
        <Card className="p-5">
          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor="choix-ecole" className="text-[12px] font-semibold text-gray-600">
              Établissement
            </label>
            <select
              id="choix-ecole"
              value={ecoleId}
              onChange={(e) => setEcoleId(e.target.value)}
              data-testid="select-ecole-vitrine"
              className="px-3 py-2 rounded-xl border border-gray-200 text-[13px] bg-white min-w-[280px]"
            >
              {chargementEcoles ? <option>Chargement…</option> : null}
              {ecoles.map((e) => (
                <option key={String(e.id)} value={String(e.id)}>
                  {e.denomination}
                </option>
              ))}
            </select>
            <span className="text-[12px] text-gray-400" data-testid="compteur-rubriques">
              {remplies} rubrique{remplies > 1 ? "s" : ""} sur {RUBRIQUES_VITRINE.length}
            </span>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="text-[14px] font-bold text-[#0F1A3C] flex items-center gap-2">
            <ImageIcon className="w-4 h-4" /> Photo de l'établissement
          </h3>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            {vitrine?.photoUrl ? (
              <img
                src={vitrine.photoUrl}
                alt="Photo de l'établissement"
                data-testid="vitrine-photo"
                className="h-28 w-48 rounded-xl object-cover border border-slate-200"
              />
            ) : (
              <div className="h-28 w-48 rounded-xl border border-dashed border-slate-300 flex items-center justify-center text-[12px] text-slate-400">
                Aucune photo
              </div>
            )}
            <div className="flex-1 min-w-[240px]">
              <p className="text-[12px] text-slate-500">
                Fond du bandeau, derrière le nom. Cadrage paysage, 2 Mo maximum, JPEG, PNG ou
                WebP. Évitez les photos où des élèves sont reconnaissables : la fiche est
                publique.
              </p>
              <input
                key={cleInput}
                id="photo-vitrine"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid="input-photo-vitrine"
                onChange={(e) => envoyerPhoto(e.target.files?.[0])}
              />
              <Button
                asChild
                variant="outline"
                className="mt-3"
                data-testid="btn-photo-vitrine"
              >
                <label htmlFor="photo-vitrine" className="cursor-pointer">
                  {envoiPhoto ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {vitrine?.photoUrl ? "Remplacer" : "Choisir une photo"}
                </label>
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="text-[14px] font-bold text-[#0F1A3C] flex items-center gap-2">
            <Store className="w-4 h-4" /> Rubriques
          </h3>
          <p className="text-[12px] text-slate-500 mt-1 mb-4">
            Une rubrique vide n'apparaît pas dans l'application : le parent ne voit jamais
            d'onglet creux.
          </p>
          <div className="space-y-5">
            {RUBRIQUES_VITRINE.map((r) => {
              const valeur = textes[r.cle];
              const trop = valeur.length > VITRINE_TEXTE_MAX;
              return (
                <div key={r.cle}>
                  <div className="flex items-baseline justify-between gap-3">
                    <label htmlFor={`rubrique-${r.cle}`} className="text-[13px] font-semibold text-[#0F1A3C]">
                      {r.titre}
                    </label>
                    <span className={`text-[11px] ${trop ? "text-red-500" : "text-slate-400"}`}>
                      {valeur.length} / {VITRINE_TEXTE_MAX}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 mb-1">{r.aide}</p>
                  <textarea
                    id={`rubrique-${r.cle}`}
                    data-testid={`texte-${r.cle}`}
                    value={valeur}
                    onChange={(e) => setTextes((p) => ({ ...p, [r.cle]: e.target.value }))}
                    rows={4}
                    className={`w-full rounded-xl border px-3 py-2 text-[13px] focus:outline-none ${
                      trop ? "border-red-400" : "border-slate-200 focus:border-slate-400"
                    }`}
                  />
                </div>
              );
            })}
          </div>
          <Button
            className="mt-5"
            onClick={enregistrer}
            disabled={enregistrement || isLoading || !ecoleId}
            data-testid="btn-enregistrer-vitrine"
          >
            {enregistrement ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </Card>
      </div>
    </SedecShell>
  );
}
